import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center hero-gradient overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 right-0 w-[800px] h-[800px] -translate-y-1/2 translate-x-1/4 rounded-full bg-[#00C896]/10 blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 items-center">
          {/* Text Content - Left */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-6 block">
                Premium Financial Consultancy
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none text-white mb-8"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Build your Startup
              <br />
              <span className="text-gradient">Grow with Ease</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl mb-10"
            >
              Startup Advisory, Fractional CFO Services, and Strategic Tax 
              Planning for the Next Generation of Founders.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-8 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)]"
              >
                Get Started
                <ArrowRight size={18} />
              </Link>
         
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/10"
            >
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">50+</p>
                <p className="text-sm text-slate-500">Startup Registered</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">250+ Cr</p>
                <p className="text-sm text-slate-500">Startups Valuation  </p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-1">100+ Lakhs</p>
                <p className="text-sm text-slate-500">Tax Planned</p>
              </div>
            </motion.div>
          </div>

          {/* Hero Image - Right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-5 relative"
          >
            <div className="relative aspect-square max-w-lg mx-auto">
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-[#00C896]/20 blur-[60px] rounded-full" />
              
              {/* 3D Crystal Bar Chart Visualization */}
              <div className="relative z-10 w-full h-full flex items-end justify-center gap-4 p-8">
                {[0.4, 0.6, 0.85, 0.7, 1].map((height, i) => (
                  <motion.div
                    key={i}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 + i * 0.1, ease: "easeOut" }}
                    className="chart-bar w-12 md:w-16 rounded-t-lg origin-bottom"
                    style={{
                      height: `${height * 100}%`,
                      background: `linear-gradient(180deg, #00C896 0%, #00C896/30 100%)`,
                      boxShadow: "0 0 30px rgba(0, 200, 150, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
                    }}
                  />
                ))}
              </div>

              {/* Decorative elements */}
              <div className="absolute top-4 right-4 w-20 h-20 border border-[#00C896]/30 rounded-full" />
              <div className="absolute bottom-8 left-4 w-12 h-12 border border-[#00C896]/20 rounded-lg rotate-45" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
